'use strict';
/* ==========================================================================
   levels/video_kit.js — shared staging for THE BIG DROP scenes (levels/video.js).

   Everything here is a pure function of the part time t (the player sets T/BF),
   so frames are scrubbable and deterministic.

     VK.cam(keys, t)            eased camera from keyframes {t, d, x, y, z, sx, sy}
                                (world point (x, y) in metres is shown at screen (sx, sy), zoom z)
     VK.withCam(c, fn, shakeXY) draw fn() in world pixels (VK.vw maps metres → world px)
     VK.world(c, …)             blueprint sky with parallax scraps + kraft ground in world space
     VK.stage(t, part, keys)    puppet positions (two-shot / inset / off), eased
     VK.cast(t, ctx, st, poses) draw both puppets + place the speech bubble in a safe zone
     VK.chapter(t, part)        chapter title card: torn away at a segment start, slid in at its end
     VK.tokens / chip / strike / label / note / check / cross    formula + paper bits
   ========================================================================== */
(function () {
  const K = 22;                                  // world px per metre (Coaster default)
  const VK = { K };
  VK.vw = Coaster.view(0, 0, K);                 // metres → world px (y up → y down)
  const HAND = "'Caveat Brush'";
  const OUTLINE = 'rgba(37,60,90,0.92)';

  /* cues from the player that may be ±Infinity (a line in another part) → finite */
  VK.fin = x => (x === Infinity ? 1e6 : x === -Infinity ? -1e6 : x);
  VK.cues = ctx => ({
    L: id => VK.fin(ctx.L(id)),
    E: id => VK.fin(ctx.E(id)),
    W: (id, w, n = 1) => VK.fin(ctx.W(id, w, n)),
  });
  VK.segIndex = seg => Game.content.script.segments.indexOf(seg);
  VK.lastKind = seg => (seg.after && seg.after.length ? 'after' : seg.question ? 'reply' : 'lines');
  /* smoothstep window: 0 before a, 1 between a+ramp and b, 0 after b+ramp */
  VK.win = (t, a, b, ramp = 0.35) => Math.min(E.inOutSine(P(t, a, a + ramp)), 1 - E.inOutSine(P(t, b, b + ramp)));

  /* ---------------- camera ---------------- */
  const CAMF = ['x', 'y', 'z', 'sx', 'sy'];
  VK.cam = function (keys, t) {
    const v = Object.assign({ sx: 960, sy: 540, z: 1, x: 0, y: 0 }, keys[0]);
    for (let i = 1; i < keys.length; i++) {
      const k = keys[i]; if (t <= k.t) continue;
      const e = E.inOutSine(P(t, k.t, k.t + (k.d ?? 1.0)));
      CAMF.forEach(f => { if (k[f] !== undefined) v[f] = lerp(v[f], k[f], e); });
    }
    return v;
  };
  /* ground-anchored framing: world x at the screen centre, ground (y = 0) at screen gy */
  VK.frame = (x, gy, z, t = 0, d = 1.0) => ({ t, d, x, y: 0, z, sx: 960, sy: gy });
  VK.apply = function (c) { g.translate(c.sx, c.sy); g.scale(c.z, c.z); g.translate(-c.x * K, c.y * K); };
  VK.withCam = function (c, fn, sh = [0, 0]) { g.save(); g.translate(sh[0], sh[1]); VK.apply(c); fn(); g.restore(); };
  VK.toScreen = (c, xm, ym) => [c.sx + c.z * K * (xm - c.x), c.sy - c.z * K * (ym - c.y)];
  VK.pxToScreen = (c, X, Y) => [c.sx + c.z * (X - c.x * K), c.sy + c.z * (Y + c.y * K)];
  /* eased follow: the mean of f over the last `lag` seconds (pure: only samples the past) */
  VK.smooth = function (f, t, lag = 0.6, n = 9) {
    let acc = 0, wsum = 0;
    for (let i = 0; i < n; i++) { const w = n - i; acc += f(t - lag * i / (n - 1)) * w; wsum += w; }
    return acc / wsum;
  };
  /* bump at the bottom of a drop */
  VK.shake = (t, t0, amp = 12, dur = 0.55, seed = 3) => shake(t, t0, amp, dur, seed);

  /* ---------------- sky + ground ---------------- */
  const CLOUDS = [[180, 150, 150, 46], [620, 250, 110, 34], [1150, 120, 170, 50], [1560, 260, 120, 36], [2050, 170, 140, 44], [2420, 90, 100, 30]];
  /* the fairground engraving with feathered edges (cached), so it prints onto the sky without a hard frame */
  let _fair = null;
  VK.fairground = function () {
    if (_fair && _fair.src === IMG.fairground) return _fair.cv;
    const im = IMG.fairground, cv = document.createElement('canvas'); cv.width = im.width; cv.height = im.height;
    const x = cv.getContext('2d'); x.drawImage(im, 0, 0);
    x.globalCompositeOperation = 'destination-in';
    const fx = x.createLinearGradient(0, 0, cv.width, 0);
    fx.addColorStop(0, 'rgba(0,0,0,0)'); fx.addColorStop(0.18, 'rgba(0,0,0,1)'); fx.addColorStop(0.82, 'rgba(0,0,0,1)'); fx.addColorStop(1, 'rgba(0,0,0,0)');
    x.fillStyle = fx; x.fillRect(0, 0, cv.width, cv.height);
    const fy = x.createLinearGradient(0, 0, 0, cv.height);
    fy.addColorStop(0, 'rgba(0,0,0,0)'); fy.addColorStop(0.25, 'rgba(0,0,0,1)'); fy.addColorStop(1, 'rgba(0,0,0,1)');
    x.fillStyle = fy; x.fillRect(0, 0, cv.width, cv.height);
    _fair = { src: im, cv }; return cv;
  };
  VK.sky = function (c, { clouds = true, fair = true } = {}) {
    Levels.title.blueprint(9);
    const px = c ? c.x * K * c.z : 0;
    if (fair && IMG.fairground) {
      // printed onto the blueprint (multiply): the engraving's pale paper vanishes, its lines stay
      const fx = 1450 - (px * 0.08) % 2600;
      const im = VK.fairground(), h = 520, w = h * im.width / im.height, y = c ? c.sy + 12 : 960;
      g.save(); g.globalCompositeOperation = 'multiply'; g.globalAlpha = 0.55;
      g.drawImage(im, fx - w / 2, y - h, w, h);
      g.restore();
    }
    if (!clouds) return;
    CLOUDS.forEach(([x, y, rx, ry], i) => {
      const X = ((x - px * 0.18) % 2600 + 2600) % 2600 - 340;
      paperPiece(blobPts(rx, ry, 60 + i, 0.08, 30), X, y, PAL.cream, { seed: 60 + i, depth: 0.6, alpha: 0.5, boil: false });
    });
  };
  /* kraft ground in world px (top edge at y = 0 m), tiled + culled to the view */
  VK.ground = function (c, { color = PAL.kraft, y = 0, seedBase = 300 } = {}) {
    const x0 = (-c.sx) / c.z + c.x * K, x1 = (1920 - c.sx) / c.z + c.x * K, TW = 1240;
    for (let i = Math.floor(x0 / TW) - 1; i <= Math.ceil(x1 / TW); i++) {
      paperRect(i * TW + TW / 2, -y * K + 1190, TW + 140, 2400, color, { seed: seedBase + ((i % 7) + 7) % 7, torn: true, tex: 'tex_kraft', depth: 1.2, boil: false });
    }
  };
  VK.world = function (c, fn, { sh = [0, 0], clouds = true } = {}) {
    VK.sky(c, { clouds });
    VK.withCam(c, () => { fn(); }, sh);
  };

  /* ---------------- puppets ---------------- */
  const POS = {
    two: { max: { x: 255, y: 1092, h: 540, dip: 0 }, voss: { x: 1665, y: 1092, h: 580, dip: 0 } },
    inset: { max: { x: 122, y: 1098, h: 300, dip: 64 }, voss: { x: 1798, y: 1098, h: 318, dip: 64 } },
    off: { max: { x: 110, y: 1520, h: 300, dip: 0 }, voss: { x: 1810, y: 1520, h: 318, dip: 0 } },
    enterR: { voss: { x: 2150, y: 1092, h: 580, dip: 0 }, max: { x: -300, y: 1092, h: 540, dip: 0 } },
  };
  VK.POS = POS;
  /* keys: [[t, modeBoth] | [t, modeMax, modeVoss] | [t, …, d]]; a mode is a POS name or {x, y, h, dip} */
  VK.stage = function (t, part, keys) {
    const out = {};
    ['max', 'voss'].forEach(who => {
      const posOf = k => { const m = who === 'max' ? k[1] : (k[2] ?? k[1]); return typeof m === 'string' ? POS[m][who] : m[who] || m; };
      const v = Object.assign({}, posOf(keys[0]));
      for (let i = 1; i < keys.length; i++) {
        const k = keys[i]; if (t <= k[0]) continue;
        const e = E.inOutCubic(P(t, k[0], k[0] + (k[3] ?? 0.7))), p = posOf(k);
        ['x', 'y', 'h', 'dip'].forEach(f => { v[f] = lerp(v[f], p[f], e); });
      }
      // an inset listener sinks a little; the speaker rises into view
      const talk = part.lines.filter(l => l.who === who).reduce((a, l) => Math.max(a, VK.win(t, l.start - 0.35, l.end + 0.15, 0.3)), 0);
      v.y += v.dip * (1 - talk);
      out[who] = v;
    });
    return out;
  };
  /* draw both puppets; poses: {max: 'cross'|'point'|null (auto), voss: 'clip'|'point'} */
  VK.cast = function (t, ctx, st, poses = {}, opts = {}) {
    const mt = ctx.caption && ctx.caption.who === 'max' ? ctx.caption.text : '';
    const maxPose = poses.max || (/[?!]/.test(mt) ? 'point' : 'cross');
    if (st.max.y - st.max.h < 1080) Puppet.draw('max', st.max.x, st.max.y, st.max.h, { env: ctx.speaking('max'), facing: 1, t, pose: maxPose });
    if (st.voss.y - st.voss.h < 1080) Puppet.draw('voss', st.voss.x, st.voss.y, st.voss.h, { env: ctx.speaking('voss'), facing: -1, t, pose: poses.voss || null });
    VK.bubble(ctx, st, opts);
  };
  /* ---------------- speech bubbles ----------------
     A bubble never grows past 2 rows: a long line first gets a wider bubble (up to opts.maxW), and if it still
     doesn't fit it is paged into ≤ 2-row chunks, split at sentence ends (then commas) and timed by the words
     (words.json): each page pops in just before its first word. Pure in t, cached per line + width. */
  // caption size: the player's (40, or ≥ 18 CSS px on a touch screen: core/player.js Player.captionSize)
  const BS = () => (window.Player && Player.captionSize ? Player.captionSize() : 40);
  const pageCache = new Map();
  VK.pages = function (l, w) {
    const size = BS(), key = l.id + '|' + w + '|' + l.tokens.length + '|' + size;
    if (pageCache.has(key)) return pageCache.get(key);
    const toks = l.tokens, n = toks.length, maxW = w - 56;
    g.save(); g.font = `${size}px ${HAND}`;
    const sp = g.measureText(' ').width, ws = toks.map(k => g.measureText(k.w).width);
    g.restore();
    const rowsOf = (a, b) => { let r = 1, rw = 0; for (let i = a; i < b; i++) { if (rw > 0 && rw + sp + ws[i] > maxW) { r++; rw = 0; } rw += ws[i] + sp; } return r; };
    const pages = [];
    let a = 0;
    while (a < n) {
      let b = a + 1; while (b < n && rowsOf(a, b + 1) <= 2) b++;
      if (b < n) {
        const lo = a + Math.max(1, Math.floor((b - a) * 0.4));
        let cut = -1;
        for (const re of [/[.!?]["')]?$/, /[,;:]$/]) { for (let j = b; j >= lo; j--) if (re.test(toks[j - 1].w)) { cut = j; break; } if (cut > 0) break; }
        if (cut > 0) b = cut;
      }
      pages.push([a, b]); a = b;
    }
    const out = pages.map(([a, b], k) => {
      const t0 = k === 0 ? 0 : Math.max(0, toks[a].s - 0.15);
      return { t0, text: toks.slice(a, b).map(x => x.w).join(' '), rows: rowsOf(a, b),
        tokens: toks.slice(a, b).map(x => ({ w: x.w, s: x.s - t0, e: x.e - t0 })) };
    });
    pageCache.set(key, out);
    return out;
  };
  /* the widest-needed layout: {w, pages, h} for a line, trying widths w0 … maxW before paging at w0 */
  VK.fit = function (l, w0, maxW = w0) {
    const BLH = BS() * 1.18;
    for (let w = w0; w <= maxW; w += 40) { const pg = VK.pages(l, w); if (pg.length === 1) return { w, pages: pg, h: pg[0].rows * BLH + 40 }; }
    const pg = VK.pages(l, w0);
    return { w: w0, pages: pg, h: Math.max(...pg.map(x => x.rows)) * BLH + 40 };
  };
  /* the page showing at line time lt */
  VK.pageAt = (pages, lt) => { let k = 0; for (let i = 1; i < pages.length; i++) if (lt >= pages[i].t0) k = i; return pages[k]; };
  /* place the caption: first candidate whose rect (tail included) stays clear of opts.avoid(u) — the action's
     screen rects at part time u — over the whole line [start − 0.2, end + 0.4]. Chosen once per line, so the
     bubble never jumps mid-line. */
  const placeCache = new Map();
  const hits = (r, q) => r[0] < q[2] && r[2] > q[0] && r[1] < q[3] && r[3] > q[1];
  function choose(l, cands, avoid, key) {
    if (!avoid || cands.length < 2) return cands[0];
    const k = key + '|' + l.id + '|' + l.start.toFixed(3) + '|' + cands.map(c => c.x.toFixed(0) + ',' + c.y.toFixed(0)).join(';');
    if (placeCache.has(k)) return cands[placeCache.get(k)];
    let best = 0, bestN = Infinity;
    for (let i = 0; i < cands.length; i++) {
      const c = cands[i], r = [c.x - c.w / 2 - 24, c.y - c.h / 2 - 24, c.x + c.w / 2 + 24, c.y + c.h / 2 + 90];
      let n = 0;
      for (let u = l.start - 0.2; u <= l.end + 0.4; u += 0.1) for (const q of avoid(u)) if (hits(r, q)) n++;
      if (n < bestN) { best = i; bestN = n; }
      if (n === 0) break;
    }
    if (placeCache.size > 400) placeCache.clear();
    placeCache.set(k, best);
    return cands[best];
  }
  /* speech bubble in a safe zone next to its speaker (never over the action):
       two-shot (tall puppet): above the head, toward the middle of the screen
       inset (small puppet):   in the bottom band beside the head
       'corridor' mode:        inset bubbles keep the centre column free
     opts.at[lineId] = {x, y, side, w} overrides a line; opts.avoid(u) → [[x0, y0, x1, y1], …] screen rects of the
     action at part time u: the bubble then moves to the first free zone (own side low, opposite upper corner, own
     upper corner, opposite low); opts.top = y where the upper zones start (below the HUD). */
  VK.bubble = function (ctx, st, opts = {}) {
    const l = ctx.caption; if (!l) return;
    if (opts.at && opts.at[l.id]) { ctx.captionAt = opts.at[l.id]; return; }
    const p = st[l.who]; if (!p) return;
    if (p.y - p.h > 1040) { ctx.captionAt = 'bottom'; return; }
    const dir = l.who === 'max' ? 1 : -1, side = dir > 0 ? 'left' : 'right';
    const tall = p.h > 420, corridor = !tall && opts.mode === 'corridor', k = BS() / 40;
    const w0 = Math.round((corridor ? 560 : opts.w || (tall ? 620 : 700)) * k);
    const fit = VK.fit(l, w0, Math.round((corridor ? 560 : opts.maxW || (tall ? 780 : 820)) * k));
    const w = fit.w, bh = fit.h;
    let cx, cy;
    if (tall) {
      const tipX = p.x + dir * 0.17 * p.h, tipY = p.y - 0.93 * p.h;
      cx = tipX + dir * (w / 2 - 10); cy = tipY - bh / 2 - 44;
    } else if (corridor) {
      // keep the centre column (≈ 720–1200 px) free for the action: the bubble sits above the inset
      // puppet's shoulder, on its own side of the screen
      cx = dir > 0 ? 150 + w / 2 : 1770 - w / 2;
      cy = 800 - bh / 2;
    } else {
      cx = p.x + dir * (0.2 * p.h + w / 2 + 16);
      cy = Math.min(866, 968 - bh / 2);
    }
    cx = Math.max(w / 2 + 16, Math.min(1904 - w / 2, cx));
    cy = Math.max(bh / 2 + 16, cy);
    let at = { x: cx, y: cy, w, h: bh };
    if (!tall && opts.avoid) {
      const top = (opts.top ?? 430) + bh / 2, ox = 1920 - cx;
      at = choose(l, [at, { x: ox, y: top, w, h: bh }, { x: cx, y: top, w, h: bh }, { x: ox, y: cy, w, h: bh }], opts.avoid, opts.key || '');
    }
    const cap = { x: at.x, y: at.y, side, w };
    if (fit.pages.length > 1) {
      const pg = VK.pageAt(fit.pages, ctx.t - l.start);
      Object.assign(cap, { text: pg.text, tokens: pg.tokens, t0: pg.t0 });
    }
    ctx.captionAt = cap;
  };

  /* ---------------- chapter card + torn transitions ---------------- */
  VK.CARD = { hold: 1.0, tear: 0.75, lead: 2.2, wipe: 0.55 };
  VK.drawCard = function (i) {
    const segs = Game.content.script.segments, seg = segs[i]; if (!seg) return;
    const n = i + 1;
    g.fillStyle = paperTile(PAL.kraft, 23, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
    paperRect(960, 548, 1790, 990, PAL.cream, { seed: 900 + n, depth: 1.6, boil: false });
    // notebook rules + red margin
    g.save(); g.strokeStyle = 'rgba(90,150,190,0.28)'; g.lineWidth = 2; g.beginPath();
    for (let y = 150; y < 1010; y += 62) { g.moveTo(90, y); g.lineTo(1830, y); }
    g.stroke(); g.strokeStyle = 'rgba(224,83,61,0.35)'; g.beginPath(); g.moveTo(250, 60); g.lineTo(250, 1036); g.stroke(); g.restore();
    tape(170, 90, 190, -0.55, undefined, 910 + n); tape(1750, 90, 190, 0.55, undefined, 920 + n);
    hand('chapter', 960, 262, 58, -1e6, { dur: 0.01, color: PAL.navy, seed: 930 + n, font: "'Special Elite'" });
    ransom(String(n), 960, 440, 230, -1e6, { seed: 700 + n });
    hand(seg.chapter, 960, 680, 132, -1e6, { dur: 0.01, color: PAL.navy, seed: 940 + n, rot: -0.02 });
    g.save(); g.font = `132px ${HAND}`; const w = g.measureText(seg.chapter).width; g.restore();
    ink([[960 - w / 2 - 10, 760], [960, 752], [960 + w / 2 + 16, 764]], { w: 8, color: PAL.red, seed: 950 + n, passes: 2 });
    // a little coaster doodle in the corner (the lesson's running gag: every chapter is a ride)
    const pts = []; for (let k = 0; k <= 40; k++) { const u = k / 40; pts.push([1380 + u * 380, 930 - Math.max(0, Math.sin(u * Math.PI * 1.6)) * 110 * (1 - u * 0.45)]); }
    ink(pts, { w: 4, color: PAL.navy, seed: 960 + n, passes: 1, step: 10 });
    paperRect(1432, 872, 44, 24, PAL.red, { seed: 970 + n, depth: 0.5, rot: -0.9 });
    hand('~ the big drop ~', 520, 930, 40, -1e6, { dur: 0.01, color: 'rgba(37,60,90,0.6)', seed: 980 + n });
  };
  function tornBand(edge, seed, dirIn) {
    const r = rng(seed + 1), inner = edge.map(q => [q[0] + dirIn * (10 + r() * 14), q[1]]);
    const band = [...edge, ...inner.reverse()];
    g.save(); pathPts(band); g.fillStyle = paperTile(PAL.white, 2); g.fill();
    g.globalAlpha = 0.35; g.strokeStyle = '#d9ccb2'; g.lineWidth = 1.2; pathPts(edge, false); g.stroke(); g.restore();
  }
  /* the card on the part of the screen left of a torn edge sweeping right → left (tearing it away) */
  VK.cardOut = function (t, t0, dur, i, seed = 17) {
    const p = E.inOutCubic(P(t, t0, t0 + dur)); if (p >= 1) return;
    const edge = tornEdgePts(lerp(1920 + 220, -260, p), seed);
    const poly = [[-100, -100], ...edge, [-100, 1180]];
    g.save(); shadowFill(poly, 2.2, PAL.paper); g.restore();          // the sheet's shadow: pre-baked path, no per-frame blur
    g.save(); pathPts(poly); g.clip(); VK.drawCard(i); g.restore();
    tornBand(edge, seed, -1);
  };
  /* the next card sliding in from the right (its torn left edge sweeps right → left) */
  VK.cardIn = function (t, t0, dur, i, seed = 29) {
    const p = E.inOutCubic(P(t, t0, t0 + dur)); if (p <= 0) return;
    const edge = tornEdgePts(lerp(1920 + 220, -260, p), seed);
    const poly = [[2100, -100], ...edge, [2100, 1180]];
    g.save(); shadowFill(poly, 2.2, PAL.paper); g.restore();
    g.save(); pathPts(poly); g.clip(); VK.drawCard(i); g.restore();
    tornBand(edge, seed, 1);
  };
  /* call last in every scene draw: the title card at the start of the segment, the next one at its end */
  VK.chapter = function (t, part) {
    const i = VK.segIndex(part.seg), C = VK.CARD;
    if (part.kind === 'lines' && t < C.hold + C.tear) {
      if (t < C.hold) { VK.drawCard(i); return; }
      VK.cardOut(t, C.hold, C.tear, i);
    }
    const segs = Game.content.script.segments;
    if (part.kind === VK.lastKind(part.seg) && i < segs.length - 1) VK.cardIn(t, part.dur - C.wipe - 0.05, C.wipe, i + 1);
  };
  VK.chapterSfx = function (part) {
    const out = [], i = VK.segIndex(part.seg), segs = Game.content.script.segments;
    if (part.kind === 'lines') out.push({ name: 'tear', t: VK.CARD.hold, gain: 0.55 });
    if (part.kind === VK.lastKind(part.seg) && i < segs.length - 1) out.push({ name: 'paper', t: part.dur - VK.CARD.wipe - 0.05, gain: 0.5 });
    return out;
  };

  /* ---------------- formula + paper bits ---------------- */
  /* ransom tokens at explicit x positions: [{s, x, t0, k (size factor), seed}] */
  VK.tokens = function (list, y, size, seed = 1, o = {}) {
    list.forEach((tk, i) => ransom(tk.s, tk.x, y + (tk.dy || 0), size * (tk.k || 1), tk.t0, { keepCase: true, seed: tk.seed ?? seed + i * 7, stagger: 0.05, exitT: o.exitT ?? null }));
  };
  /* handwritten label (cream with a navy outline so it reads on blueprint and on paper) */
  VK.label = function (str, x, y, size, t0, o = {}) {
    hand(str, x, y, size, t0, Object.assign({ color: PAL.cream, outline: OUTLINE, seed: 5 }, o));
  };
  /* dashed slot waiting for a number */
  VK.slot = function (x, y, w, h, alpha = 1) {
    if (alpha <= 0) return;
    g.save(); g.globalAlpha *= alpha; g.setLineDash([10, 8]); g.lineDashOffset = -(BF % 4) * 4;
    g.strokeStyle = 'rgba(251,245,230,0.85)'; g.lineWidth = 3; g.strokeRect(x - w / 2, y - h / 2, w, h); g.restore();
  };
  /* a number on a paper chip that drops into place at t0 (falls, lands, squashes) */
  VK.font = (str, size) => (/[a-z]/.test(str) ? `${Math.round(size * 0.92)}px 'Alfa Slab One'` : `${size}px 'Luckiest Guy'`);   // caps-only font can't say "kg"
  VK.chip = function (str, x, y, size, t0, o = {}) {
    const t = T; if (t < t0) return;
    const fall = 0.32, p = P(t, t0, t0 + fall);
    const dy = -300 * (1 - p * p);
    const sq = t > t0 + fall ? spring(t, t0 + fall, 3.2, 8) : 0;
    g.save(); g.font = VK.font(str, size);
    const w = g.measureText(str).width + size * 0.7, h = size * 1.25;
    g.translate(x, y + dy); g.rotate((1 - p) * 0.35 * (o.spin ?? 1) + (o.rot ?? 0));
    g.scale(1 + sq * 0.12, 1 - sq * 0.16);
    paperRect(0, 0, w, h, o.color || PAL.white, { seed: o.seed || 3, depth: 1.0 });
    g.fillStyle = o.ink || PAL.navy; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText(str, 0, size * 0.07);
    g.restore();
  };
  /* red-ink crossing-out (drawn on, two passes) */
  VK.strike = function (x0, y0, x1, y1, prog, seed = 1, color = PAL.red, w = 9) {
    ink([[x0, y0], [(x0 + x1) / 2 + 4, (y0 + y1) / 2 - 3], [x1, y1]], { w, color, seed, prog, passes: 2, step: 10 });
  };
  VK.check = function (x, y, s, prog, seed = 1) {
    ink([[x - 34 * s, y - 2 * s], [x - 8 * s, y + 26 * s], [x + 40 * s, y - 38 * s]], { w: 11 * s, color: PAL.teal, seed, prog, passes: 2, step: 8 });
  };
  VK.cross = function (x, y, s, prog, seed = 1) {
    ink([[x - 30 * s, y - 30 * s], [x + 30 * s, y + 30 * s]], { w: 11 * s, color: PAL.red, seed, prog: clamp(prog * 2), passes: 2, step: 8 });
    ink([[x + 30 * s, y - 30 * s], [x - 30 * s, y + 30 * s]], { w: 11 * s, color: PAL.red, seed: seed + 3, prog: clamp(prog * 2 - 1), passes: 2, step: 8 });
  };
  /* sticky note with the viewer's answer, then ✓ or ✗ */
  VK.answerNote = function (ctx, part, x, y, t0, tMark, o = {}) {
    const q = ctx.question; if (!q) return;
    const ans = ctx.answer != null ? ctx.answer : part.answer != null ? part.answer : q.correct;
    const s = pop(T, t0, 0.35); if (s <= 0) return;
    const w = o.w || 380, lines = UI._wrap(q.options[ans], w - 60, `36px ${HAND}`), h = 96 + lines.length * 40;
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.04); g.scale(s, s);
    paperRect(0, 0, w, h, PAL.gold, { seed: 520, depth: 1.3 });
    g.restore();
    tape(x, y - h / 2 + 4, 110, 0.05, undefined, 521);
    hand('you said:', x - w / 2 + 28, y - h / 2 + 42, 30, t0 + 0.1, { align: 'left', color: 'rgba(43,36,32,0.65)', seed: 522, rot: o.rot ?? -0.04 });
    lines.forEach((l, i) => hand(l, x, y - h / 2 + 84 + i * 40, 36, t0 + 0.25 + i * 0.2, { color: PAL.ink, seed: 523 + i, rot: o.rot ?? -0.04 }));
    const right = ans === q.correct, mp = P(T, tMark, tMark + 0.35);
    if (right) VK.check(x + w / 2 - 20, y - h / 2 + 10, 1.2, mp, 530);
    else VK.cross(x + w / 2 - 24, y - h / 2 + 14, 1.0, mp, 530);
    return right;
  };
  /* a small paper tag with a string (for masses, heights, labels that follow a cart) */
  VK.tag = function (str, x, y, size, s = 1, o = {}) {
    if (s <= 0) return;
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.05); g.scale(s, s);
    g.font = VK.font(str, size);
    const w = g.measureText(str).width + size * 0.8, h = size * 1.35;
    paperRect(0, 0, w, h, o.color || PAL.cream, { seed: o.seed || 41, depth: 1.1, outline: true });
    paperCircle(-w / 2 + size * 0.3, 0, size * 0.1, PAL.kraft, { seed: (o.seed || 41) + 1, depth: 0 });
    g.fillStyle = o.ink || PAL.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(str, size * 0.12, size * 0.07);
    g.restore();
  };
  /* dashed "energy line" at a height, across [x0, x1] metres (world px space) */
  VK.dashed = function (x0, x1, ym, color = 'rgba(251,245,230,0.8)', w = 4) {
    g.save(); g.setLineDash([18, 12]); g.lineDashOffset = -(BF % 4) * 6; g.strokeStyle = color; g.lineWidth = w;
    g.beginPath(); g.moveTo(x0 * K, -ym * K); g.lineTo(x1 * K, -ym * K); g.stroke(); g.restore();
  };
  /* ink sweat drops + strain lines around a point (screen px), pure in t */
  VK.strain = function (x, y, r, t, t0, amt = 1, seed = 1) {
    if (t < t0 || amt <= 0) return;
    const rr = rng(seed);
    for (let i = 0; i < 7; i++) {
      const a = -Math.PI * (0.1 + 0.8 * i / 6) + (rr() - 0.5) * 0.2, fl = 0.5 + 0.5 * Math.sin(t * 14 + i * 2.1);
      const r0 = r * (1.02 + 0.04 * fl), r1 = r * (1.2 + 0.08 * fl);
      ink([[x + Math.cos(a) * r0, y + Math.sin(a) * r0], [x + Math.cos(a) * r1, y + Math.sin(a) * r1]], { w: 5, seed: seed + i, alpha: amt, step: 20, passes: 1 });
    }
    // sweat drops: one every 0.28 s, each flies out on an arc and fades
    const per = 0.28, k0 = Math.floor((t - t0) / per);
    for (let k = Math.max(0, k0 - 3); k <= k0; k++) {
      const age = t - t0 - k * per; if (age < 0 || age > 0.84) continue;
      const side = h2(seed, k) < 0.5 ? -1 : 1, a = -Math.PI / 2 + side * (0.5 + h2(seed + 1, k) * 0.7);
      const sp = r * 1.4, px = x + Math.cos(a) * (r * 0.9 + sp * age), py = y + Math.sin(a) * (r * 0.9 + sp * age) + 260 * age * age;
      const al = amt * (1 - age / 0.84), sz = r * 0.07;
      g.save(); g.globalAlpha *= al; g.translate(px, py); g.rotate(a + Math.PI / 2);
      g.beginPath(); g.moveTo(0, -sz * 1.8); g.quadraticCurveTo(sz * 1.1, 0, 0, sz); g.quadraticCurveTo(-sz * 1.1, 0, 0, -sz * 1.8);
      g.fillStyle = PAL.sky; g.fill(); g.lineWidth = 2; g.strokeStyle = PAL.navy; g.stroke(); g.restore();
    }
  };
  /* film-style tag ("◀◀ REWIND", "SLOW-MO ×½", "FREEZE") */
  VK.filmTag = function (str, x, y, s, color = PAL.red) {
    if (s <= 0) return;
    g.save(); g.translate(x, y); g.rotate(-0.04); g.scale(s, s);
    g.font = "40px 'Special Elite'"; const w = g.measureText(str).width + 50;
    paperRect(0, 0, w, 64, color, { seed: 77, depth: 1.1 });
    g.fillStyle = PAL.cream; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(str, 0, 3);
    g.restore();
  };

  window.VK = VK;
})();
